/**
 * Copy text to the clipboard, including on insecure origins.
 *
 * `navigator.clipboard` is only exposed in a **secure context** — HTTPS, or `localhost`. On the LAN
 * demo/prod host the app is served over plain HTTP at `http://<lan-ip>:3105`, so the whole
 * `navigator.clipboard` object is `undefined` there and every `navigator.clipboard.writeText(…)`
 * call throws a TypeError. Call sites that wrapped it in `try/catch` (or `?.`) then failed
 * *silently*: the button never flipped to "Đã sao chép", with nothing in the console to explain it.
 *
 * So: try the modern API first, and fall back to the legacy `document.execCommand("copy")` over a
 * throwaway off-screen textarea, which still works on insecure origins in every browser we target.
 *
 * @returns `true` when the text reached the clipboard, `false` when both paths failed — callers
 *   should only show their "copied ✓" state on `true`, and can surface a hint on `false`.
 */
export async function copyText(text: string): Promise<boolean> {
  // Secure-context path (HTTPS / localhost).
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Permission denied, or a non-focused document — fall through to the legacy path.
    }
  }

  // Legacy path — works over plain HTTP.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    // Off-screen but still focusable/selectable; `readOnly` stops the mobile keyboard popping up.
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);

    const selection = document.getSelection();
    const previous = selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    const ok = document.execCommand("copy");

    document.body.removeChild(ta);
    // Restore whatever the user had selected before we hijacked the selection.
    if (previous && selection) {
      selection.removeAllRanges();
      selection.addRange(previous);
    }
    return ok;
  } catch {
    return false;
  }
}
